// contexts/ProjectContext.js — Fase 9: Multi-Proyecto
// Detecta el proyecto actual por dominio/subdominio y expone su info al frontend
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';
import { permiteCategoria } from '../utils/consentimiento';

const ProjectContext = createContext(null);

const urlIcono = (ruta) => (ruta.startsWith('http') ? ruta : `/backend/${ruta.replace(/^\//, '')}`);

const tipoIcono = (ruta) => {
  const ext = (ruta.split('.').pop() || '').toLowerCase();
  if (ext === 'svg') return 'image/svg+xml';
  if (ext === 'ico') return 'image/x-icon';
  if (ext === 'jpg') return 'image/jpeg';
  return ext ? `image/${ext}` : undefined;
};

/**
 * Aplica los iconos del proyecto en toda la página, reemplazando los estáticos
 * del index.html y los del proyecto anterior.
 *
 * Además del favicon de siempre, el proyecto puede dar los de cada dispositivo
 * (`config_json.iconos`): el de 192px para Android, el de iOS al añadir a la
 * pantalla de inicio y el mosaico de Windows. Lo que no defina se cae al
 * favicon base, así que un proyecto con un solo icono se comporta igual que
 * antes.
 */
const applyFavicon = (faviconPath, iconos = {}) => {
  const base = faviconPath || iconos.favicon_32 || iconos.favicon_192 || '';
  if (!base && !iconos.apple_touch && !iconos.mstile) return;

  document.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"], link[data-project-favicon], meta[data-project-favicon]')
    .forEach(el => el.remove());

  const poner = (rel, ruta, sizes) => {
    if (!ruta) return;
    const link = document.createElement('link');
    link.rel = rel;
    const type = tipoIcono(ruta);
    if (type) link.type = type;
    if (sizes) link.setAttribute('sizes', sizes);
    link.href = urlIcono(ruta);
    link.setAttribute('data-project-favicon', 'true');
    document.head.appendChild(link);
  };
  const ponerMeta = (name, contenido) => {
    if (!contenido) return;
    const meta = document.createElement('meta');
    meta.name = name;
    meta.content = contenido.startsWith('#') ? contenido : urlIcono(contenido);
    meta.setAttribute('data-project-favicon', 'true');
    document.head.appendChild(meta);
  };

  poner('icon', iconos.favicon_32 || base, iconos.favicon_32 ? '32x32' : undefined);
  poner('icon', iconos.favicon_192, '192x192');
  poner('apple-touch-icon', iconos.apple_touch || iconos.favicon_192 || base, '180x180');
  ponerMeta('msapplication-TileImage', iconos.mstile);
  ponerMeta('msapplication-TileColor', iconos.color_mosaico);
};

// Título de la pestaña + meta description (y og:*) según el proyecto.
const setMetaTag = (selector, attr, value) => {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, name] = selector.match(/\[(?:name|property)="([^"]+)"\]/) || [];
    if (selector.includes('property=')) el.setAttribute('property', name);
    else el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const applyMeta = (titulo, descripcion) => {
  if (titulo) document.title = titulo;
  if (descripcion) setMetaTag('meta[name="description"]', 'content', descripcion);
  if (titulo) setMetaTag('meta[property="og:title"]', 'content', titulo);
  if (descripcion) setMetaTag('meta[property="og:description"]', 'content', descripcion);
};

// Inyecta scripts personalizados del proyecto (Bitrix24, GA, píxeles, chats…) en
// el <head>. Un <script> insertado con innerHTML/dangerouslySetInnerHTML NO se
// ejecuta; hay que recrear cada nodo <script> para que el navegador lo corra.
const injectHeaderScripts = (rawHtml, categoria = 'necesarias') => {
  if (!rawHtml || typeof rawHtml !== 'string' || !rawHtml.trim()) return;
  // Evitar doble inyección (StrictMode en dev o remounts). Cada categoría lleva
  // su propia marca porque se inyectan en momentos distintos (al aceptar).
  if (document.querySelector(`[data-project-scripts="${categoria}"]`)) return;
  const tpl = document.createElement('template');
  tpl.innerHTML = rawHtml;
  Array.from(tpl.content.childNodes).forEach(node => {
    if (node.nodeName === 'SCRIPT') {
      const s = document.createElement('script');
      Array.from(node.attributes || []).forEach(a => s.setAttribute(a.name, a.value));
      if (node.textContent) s.textContent = node.textContent;
      s.setAttribute('data-project-scripts', categoria);
      document.head.appendChild(s);
    } else if (node.nodeType === 1) {
      // Otros elementos (noscript, iframe de fallback, etc.)
      node.setAttribute('data-project-scripts', categoria);
      document.head.appendChild(node);
    }
  });
};

// Área de la app según la ruta actual (para deshabilitar scripts por modo).
const areaFromLocation = (pathname = '', search = '') => {
  if (/[?&]modo=practica\b/.test(search)) return 'practica';
  if (pathname.startsWith('/mentor')) return 'mentor';
  if (pathname.startsWith('/evaluacion')) return 'evaluacion';
  if (pathname.startsWith('/consulta')) return 'consulta';
  if (pathname.startsWith('/reto')) return 'reto';
  return 'publico';
};

// Oculta (por CSS) los widgets ya cargados en las rutas excluidas. `selector`
// lo define el admin (ej. el contenedor flotante del chat). Sin selector no
// oculta nada visualmente (útil sobre todo para píxeles/analytics).
const applyHideToggle = (hide, selector) => {
  const id = 'project-scripts-hide';
  let styleEl = document.getElementById(id);
  const sel = (selector && selector.trim()) ? selector.trim() : null;
  if (hide && sel) {
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = id;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `${sel}{display:none!important;visibility:hidden!important;}`;
  } else if (styleEl) {
    styleEl.textContent = '';
  }
};

// Sincroniza los scripts del proyecto con la ruta actual: inyecta en las áreas
// permitidas y (si el admin lo configuró) oculta el widget en las excluidas.
// Si el usuario entra directo a un área excluida, los scripts nunca se inyectan.
export const syncProjectScripts = (proyecto, pathname, search) => {
  const cfg = proyecto?.config_json?.landing_config || {};
  // Bloques de scripts: el de siempre (con su categoría, 'necesarias' por
  // defecto) más los que el admin agregue por categoría.
  const bloques = [
    { categoria: cfg.header_scripts_categoria || 'necesarias', html: cfg.header_scripts },
    ...(Array.isArray(cfg.scripts_por_categoria) ? cfg.scripts_por_categoria : []),
  ].filter(b => b && typeof b.html === 'string' && b.html.trim());
  if (bloques.length === 0) return;
  const excluir = Array.isArray(cfg.header_scripts_excluir) ? cfg.header_scripts_excluir : [];
  const area = areaFromLocation(pathname, search);
  const blocked = area !== 'publico' && excluir.includes(area);
  const sel = cfg.header_scripts_ocultar_selector;
  const yaInyectado = !!document.querySelector('[data-project-scripts]');

  if (blocked) {
    // El script ya se inyectó y su widget (Bitrix, chat, etc.) está vivo en el DOM.
    // Un script de terceros ya ejecutado NO se puede "descargar" de forma fiable en
    // una SPA: si venimos de la landing con el widget cargado y entramos a un área
    // excluida, hay que recargar para entrar limpio (idéntico a una entrada directa,
    // donde el script nunca se inyecta). Excepción: si el admin definió un selector,
    // basta con ocultarlo por CSS sin recargar.
    if (yaInyectado && (!sel || !sel.trim())) {
      window.location.reload();
      return;
    }
    applyHideToggle(true, sel);
    return;
  }

  // Área permitida: mostrar (des-ocultar) e inyectar lo que el visitante haya
  // consentido. Sin banner de cookies activo, entra todo como siempre.
  applyHideToggle(false, sel);
  bloques.forEach(b => {
    if (!permiteCategoria(proyecto, b.categoria)) return;
    injectHeaderScripts(b.html, b.categoria || 'necesarias');
  });
};

export const ProjectProvider = ({ children }) => {
  const [proyecto, setProyecto] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProyecto = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/proyecto-info.php`);
        if (res.data?.success && res.data.proyecto) {
          setProyecto(res.data.proyecto);

          // Aplicar colores como CSS custom properties
          const p = res.data.proyecto;
          if (p.color_primario) {
            document.documentElement.style.setProperty('--project-primary', p.color_primario);
          }
          if (p.color_secundario) {
            document.documentElement.style.setProperty('--project-secondary', p.color_secundario);
          }
          // Al expirar la sesión: ir a esta dirección sin el aviso (lo lee el interceptor de api.js).
          window.__sesionExpiradaDestino = p.config_json?.auth_config?.sesion_expirada_destino || '';
          // Favicon del proyecto en toda la página
          applyFavicon(p.favicon, p.config_json?.iconos || {});
          // Título de la pestaña + meta description (override en config_json.seo, si no el nombre/descripción del proyecto)
          const seo = p.config_json?.seo || {};
          applyMeta(
            seo.titulo || p.config_json?.meta_titulo || p.nombre,
            seo.descripcion || p.config_json?.meta_descripcion || p.descripcion
          );
          // Los scripts personalizados se inyectan según la ruta desde AppContent
          // (syncProjectScripts), para poder deshabilitarlos por modo.
        }
      } catch (err) {
        // Sin proyecto = landing default MentorIA (no es error)
        console.log('ProjectContext: sin proyecto detectado para este dominio');
      } finally {
        setLoading(false);
      }
    };

    fetchProyecto();
  }, []);

  return (
    <ProjectContext.Provider value={{ proyecto, loading }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const ctx = useContext(ProjectContext);
  if (ctx === null) {
    return { proyecto: null, loading: false };
  }
  return ctx;
};

export default ProjectContext;
